/**
* @typedef {{
  *  x: Number, 
  *  y: Number
  * }} vector2d
  * 
  * @typedef {{
  *  p1: vector2d,
  *  p2: vector2d
  * }} line2d
  */

import ZMath from "./ZMath";

 
class ZVector2D {
 
   /**
    * 
    * @param {vector2d} pos 
    * @param {Number} deg 
    * @returns 
    */
  static Rotate(pos, deg){
    let {x, y} = pos;
    let rad = ZMath.Deg2Rad(deg);
    return {
      x: x * Math.cos(rad) - y * Math.sin(rad),
      y: x * Math.sin(rad) + y * Math.cos(rad)
    };
  }
 
   /**
    * 
    * @param {vector2d} pos 
    * @param {vector2d} offset 
    */
  static Shift(pos, offset){
    return {
      x: pos.x + offset.x,
      y: pos.y + offset.y
    };
  }
 
   /**
    * 
    * @param {vector2d} pos 
    * @param {vector2d} offset 
    */
  static Shear(pos, ratio){
    return {
       x: pos.x * ratio.x,
       y: pos.y * ratio.y
    };
  }
 
  /**
  * 
  * @param {vector2d} a 
  * @param {vector2d} b 
  */
  static Distance(a, b){
    return Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2));
  }

  /**
  * 
  * @param {vector2d} a 
  * @param {vector2d} b center
  */
  static Angle(a, b){
    let rtn = ZMath.Rad2Deg(Math.atan2(a.y - b.y, a.x - b.x)) + 90;
    if(rtn < 0) rtn += 360;
    return rtn;
  }
 
  /**
  * The function that returns true if
  * line segment 'p1q1' and 'p2q2' intersect.
  * @param {vector2d} p1 
  * @param {vector2d} q1 
  * @param {vector2d} p2 
  * @param {vector2d} q2 
  * @returns 
  */
  static IsIntersect(p1, q1, p2, q2){
    // Find the four orientations needed for
    // general and special cases
    let o1 = this.Orientation(p1, q1, p2);
    let o2 = this.Orientation(p1, q1, q2);
    let o3 = this.Orientation(p2, q2, p1);
    let o4 = this.Orientation(p2, q2, q1);

    // General case
    if(o1 !== o2 && o3 !== o4) return true;
    // Special Cases
    // p1, q1 and p2 are collinear and
    // p2 lies on segment p1q1
    if (o1 === 0 && this.IsOnSegment(p1, p2, q1)) return true;

    // p1, q1 and p2 are collinear and
    // q2 lies on segment p1q1
    if (o2 === 0 && this.IsOnSegment(p1, q2, q1)) return true;

    // p2, q2 and p1 are collinear and
    // p1 lies on segment p2q2
    if (o3 === 0 && this.IsOnSegment(p2, p1, q2)) return true;

    // p2, q2 and q1 are collinear and
    // q1 lies on segment p2q2
    if (o4 === 0 && this.IsOnSegment(p2, q1, q2)) return true;

    // Doesn't fall in any of the above cases
    return false;
  }

  /**
  * Given three collinear points p, q, r,
  * the function checks if point q lies
  * on line segment 'pr'
  * @param {vector2d} p 
  * @param {vector2d} q
  * @param {vector2d} r
  */
  static IsOnSegment(p, q, r){
    return (q.x <= Math.max(p.x, r.x) 
      && q.x >= Math.min(p.x, r.x) 
      && q.y <= Math.max(p.y, r.y) 
      && q.y >= Math.min(p.y, r.y));
  }

  /**
  * triplet (p, q, r) ->
  * 0 -> collinear
  * 1 -> Clockwise
  * 2 -> Anti-clockwise
  * @param {vector2d} p 
  * @param {vector2d} q 
  * @param {vector2d} r 
  */
  static Orientation(p, q, r){
    let v = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
    if(v === 0) return 0;
    return v > 0 ? 1 : 2;
  }

  /**
   * Ray-Casting Theory (From GPT)
   * @param {vector2d} point 
   * @param {vector2d[]} areaVertices 
   */
  static IsInArea(point, areaVertices){
    let {x, y} = point;
    let inside = false;

    for (let i = 0, j = areaVertices.length - 1; i < areaVertices.length; j = i++) {
      let xi = areaVertices[i].x, yi = areaVertices[i].y;
      let xj = areaVertices[j].x, yj = areaVertices[j].y;

      let intersect = ((yi > y) !== (yj > y)) &&
          (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }

    return inside;
  }

}
 
export default ZVector2D;